import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CurrentUser } from '@td/shared';
import { patch, post } from '../lib/api';

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { displayName: string }) => patch<CurrentUser>('/auth/profile', input),
    onSuccess: (user) => qc.setQueryData(['auth', 'me'], user),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { current: string; next: string }) =>
      post<{ ok: true }>('/auth/password', input),
  });
}

/**
 * Downloads the export.
 *
 * A plain link would work, but the request has to carry the session cookie and
 * the filename comes from the response header — fetching it and handing the
 * blob to a temporary link keeps both.
 */
export async function downloadBackup(kind: 'records' | 'archive' = 'records'): Promise<void> {
  const res = await fetch(kind === 'archive' ? '/api/backup/archive' : '/api/backup', {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('The export could not be created.');

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const stamp = new Date().toISOString().slice(0, 10);

  const a = document.createElement('a');
  a.href = url;
  a.download = `teacher-dashboard-${stamp}.${kind === 'archive' ? 'zip' : 'json'}`;
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Freed on the next tick so the download has taken hold of it first.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface RestoreResult {
  restored: Record<string, number>;
  skipped: string[];
  exportedAt: string | null;
}

/**
 * Puts a backup file back, replacing what is there.
 *
 * The confirmation is sent with it rather than checked here: a guard that
 * lives only in the browser is not a guard.
 */
export async function restoreBackup(file: File, confirm: string): Promise<RestoreResult> {
  const body = new FormData();
  body.append('file', file);
  body.append('confirm', confirm);

  const res = await fetch('/api/backup/restore', {
    method: 'POST',
    credentials: 'include',
    body,
  });

  const payload = (await res.json().catch(() => ({}))) as RestoreResult & { error?: string };
  if (!res.ok) throw new Error(payload.error ?? 'The backup could not be restored.');

  return payload;
}
